import IOperation from "app/models/Operation";
import AccountStatuses from "app/types/AccountStatuses";
import FileFormats from "app/types/FileFormats";
import Permissions from "app/types/Permissions";
import { Theme } from '@mui/material';
import { TemplateDataType, TemplateLayout, TemplateType } from "app/types/Template";
import i18n from "i18n";

export const toCamelCase = (text:string) => {
  text = text.replace(/[-_\s.]+(.)?/g, (_, c) => c ? c.toUpperCase() : '');
  return text.substring(0, 1).toLowerCase() + text.substr(1);
}

export const toSize = (bytes:number, decimals = 2) => {
  if ( bytes === 0 ) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export const toNumberWithCommas = (num:number, decimals:boolean = true) => {
  const fractionDigits = decimals ? 2 : 0;
  return num.toFixed(fractionDigits).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export const getSearchParam = (search:string, field:string):string|null => {
  const searchParams = new URLSearchParams(search);
  return searchParams.get(field) || null;
}

export const getFileExtFromFileName = (name:string):FileFormats => {
  const nameParts = name.split('.');
  let ext = (nameParts.at(-1) || '').toLowerCase();
  if (ext === 'jpg') ext = 'jpeg';
  return ext as FileFormats;
}

export const parseToken = (token:string) => {
  const tokenId = token.split('.')[1];
  try {
    return JSON.parse(atob(tokenId));
  } catch(e) {}
  return null;
}

export const downloadFile = async (response:Response, file:{
  name: string;
  ext: FileFormats
}) => {
  const blob:Blob = await response.blob();
  const filename = response.headers.get('X-Filename');
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = filename ? decodeURIComponent(filename) : `${file.name}-${new Date().getTime()}.${file.ext}`;
  link.href = url;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const permissionToNormalLabel = (permission:Permissions):string => {
  if ( permission.includes(':') ){
    return permission.split(':').reduce((acc:string, cur:string, index:number) => {
      const text = index === 0 ? `${cur.charAt(0).toUpperCase() + cur.slice(1)}` : ` ${cur}`;
      acc += text;
      return acc;
    }, '');
  }
  const label = permission.replace(/([A-Z])/g, ' $1').toLowerCase();
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export const trimValue = (value:string) => {
  return value.replace(/\s+/g, ' ').trim();
}

export const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export const textToCapitalizeCase = (text: string) => {
  return text.replace(/^\w/, c => c.toUpperCase());
}

export const camelCaseToNormalText = (text:string) => {
  const spacedString = text.replace(/([A-Z])/g, ' $1');
  const normalizedText = spacedString.trim();
  return normalizedText.charAt(0).toUpperCase() + normalizedText.slice(1);
}

export const getFontSize = (value: string):string|undefined => {
  switch (value) {
    case '1':
      return '36pt';
    case '2':
      return '28pt';
    case '3':
      return '24pt';
    case '4':
      return '20pt';
    case '5':
      return '18pt';
    case '6':
      return '16pt';
    default:
      return undefined;
  }
}

export const hasOperationByPathType = (operations:IOperation[], pathType:string) => {
  let hasOperation = false;
  for ( let operation of operations ){
    if ( operation.path.includes(pathType) ){
      hasOperation = true;
      break;
    }
  }
  return hasOperation;
}

// For testing
type Types = 'element' | 'control' | 'action' | 'active';
type Elem = 'autocomplete' | 'input' | 'select' | 'checkbox' | 'fab' | 'button' | 'button-tab' | 'list' | 'menu-item' | 'chip' | 'box-color' | 'rte';

export const generateAttrForTesting = (type:Types, elem:Elem, value:string | boolean | undefined) => {
  value = typeof value === 'string'
    ? toCamelCase(value)
    : typeof value === 'boolean' ? value : elem;
  return { [`data-${type}-${elem}`]: value }
};

export const getAccountStatusLabel = (status:AccountStatuses):string => {
  const accountStatuses = {
    [AccountStatuses.Active]: i18n.t('pages.companyAccounts.active'),
    [AccountStatuses.Disabled]: i18n.t('pages.companyAccounts.disabled'),
    [AccountStatuses.Suspended]: i18n.t('pages.companyAccounts.suspended')
  }

  return accountStatuses[status];
}

export const getTemplateTypeLabel = (type: TemplateType):string => {
  const templateTypeLabels:Record<TemplateType, string> = {
    [TemplateType.Data]: i18n.t('labels.data'),
    [TemplateType.Text]: i18n.t('labels.text')
  }

  return templateTypeLabels[type];
}

export const getTemplateDataTypeLabel = (dataType: TemplateDataType):string => {
  const templateDataTypeLabels:Record<TemplateDataType, string> = {
    [TemplateDataType.Episode]: i18n.t('labels.episode'),
    [TemplateDataType.Document]: i18n.t('labels.document')
  }

  return templateDataTypeLabels[dataType];
}

export const getTemplateLayoutLabel = (layout: TemplateLayout):string => {
  const templateLayoutLabels:Record<TemplateLayout, string> = {
    [TemplateLayout.Chart]: i18n.t('labels.chart'),
    [TemplateLayout.Custom]: i18n.t('labels.custom'),
    [TemplateLayout.Table]: i18n.t('labels.table')
  }

  return templateLayoutLabels[layout];
}

const extractPathFromUrl = (input:string) => {
  try {
    const url = new URL(input);
    return url.pathname;
  } catch (e) {
    return input;
  }
}

export const urlPathMatches = (regExp:RegExp, method:string, url:string) => {
  const path = extractPathFromUrl(url);
  const route = `${method.toUpperCase()} ${path}`;
  return regExp.test(route);
}

export const truncateContent = (content:string, maxChars:number = 100, maxRows:number = 2) => {
  let lines = content.split('\n');
  let result = [];
  let currentTextLength = 0;

  for ( let i = 0; i < lines.length; i++ ){
    let line = lines[i];
    if ( currentTextLength + line.length > maxChars ){
      let remainingChars = maxChars - currentTextLength;
      result.push(line.slice(0, remainingChars) + ' ...');
      break;
    } else {
      result.push(line);
      currentTextLength += line.length;
    }
    if ( result.length === maxRows ){
      result[maxRows - 1] = result[maxRows - 1] + ' ...';
      break;
    }
  }

  return result.join('\n');
}


export const statusThemeColor = (status:string, theme:Theme):string => {
  const info:string[] = ['active', 'sent', 'running', 'generating'];
  const warning:string[] = ['pending']
  const success:string[] = ['completed', 'published', 'paid'];
  const error:string[] = ['closed', 'outstanding', 'failed'];

  if ( info.includes(status) ) return theme.palette.info.main;
  if ( warning.includes(status) ) return theme.palette.warning.main;
  if ( success.includes(status) ) return theme.palette.success.main;
  if ( error.includes(status) ) return theme.palette.error.main;
  return theme.palette.grey[700];
}

export const generateLowQualityBorderSX = (annotations: any, height: number = 2) => {
  if (!annotations || !annotations.length) return {};
  const hasLowQualityAnnotation = annotations.some((annotation: any) => annotation.name === 'quality' && annotation.value === 'low');
  return hasLowQualityAnnotation ? {
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      bottom: 0,
      width: '100%',
      height,
      bgcolor: 'red',
      zIndex: 1
    }
  } : {};
}

export const getBrowserTimezone = ():string => {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezoneAliasMap[browserTz] || browserTz;
}

// Timezone aliases to handle browser timezone discrepancies
const timezoneAliasMap:Record<string, string> = {
  'Europe/Kiev': 'Europe/Kyiv',
  'Asia/Calcutta': 'Asia/Kolkata',
  'America/Argentina/Buenos_Aires': 'America/Buenos_Aires',
  'Asia/Rangoon': 'Asia/Yangon',
  'Pacific/Samoa': 'Pacific/Pago_Pago',
  'Asia/Ulan_Bator': 'Asia/Ulaanbaatar',
  'Asia/Tel_Aviv': 'Asia/Jerusalem',
  'Europe/Tiraspol': 'Europe/Chisinau',
  'Africa/Asmera': 'Africa/Asmara',
  'America/Atka': 'America/Adak',
  'US/Eastern': 'America/New_York',
  'US/Central': 'America/Chicago',
  'US/Mountain': 'America/Denver',
  'US/Pacific': 'America/Los_Angeles',
  'US/Arizona': 'America/Phoenix',
  'US/Alaska': 'America/Anchorage',
  'US/Hawaii': 'Pacific/Honolulu',
  'Canada/Eastern': 'America/Toronto',
  'Canada/Central': 'America/Winnipeg',
  'Canada/Mountain': 'America/Edmonton',
  'Canada/Pacific': 'America/Vancouver',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Australia/ACT': 'Australia/Sydney',
  'Australia/NSW': 'Australia/Sydney',
  'Australia/North': 'Australia/Darwin',
  'Australia/Queensland': 'Australia/Brisbane',
  'Australia/South': 'Australia/Adelaide',
  'Australia/Tasmania': 'Australia/Hobart',
  'Australia/Victoria': 'Australia/Melbourne',
  'Australia/West': 'Australia/Perth',
  'Australia/Yancowinna': 'Australia/Broken_Hill',
};

export const normalizeQuillContent = (html:string):string => {
  if (!html) return html;

  return html
    // Ensure a paragraph break between adjacent tables
    .replace(/<\/table>\s*<table/g, '</table><p><br></p><table')
    // Preserve multiple consecutive spaces by converting them to &nbsp;
    .replace(/ {2,}/g, match => '&nbsp;'.repeat(match.length));
};
